import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SETTINGS_ROLES } from "@/lib/nav";

const fmtMin = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const CH_META = {
  portal: { label: "Secure Portal", icon: "Globe", tone: "text-violet-600" },
  webchat: { label: "Website Chat", icon: "MessageSquare", tone: "text-emerald-600" },
  website_form: { label: "Website Form", icon: "FileInput", tone: "text-sky-600" },
  google_business: { label: "Google Business", icon: "Chrome", tone: "text-rose-600" },
  email: { label: "Email", icon: "Mail", tone: "text-blue-600" },
  sms: { label: "SMS", icon: "MessageCircle", tone: "text-amber-600" },
  whatsapp: { label: "WhatsApp", icon: "MessageCircle", tone: "text-green-600" },
  line: { label: "LINE", icon: "MessageCircle", tone: "text-teal-600" },
  phone_note: { label: "Phone Calls", icon: "Phone", tone: "text-slate-600" },
};

function Kpi({ label, value, sub, icon, tone = "text-slate-900", ring = "ring-slate-200", onClick, testid }) {
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left bg-white border border-slate-200 rounded-xl p-4 ring-1 ${ring} hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide leading-tight">{label}</span>
        <Ic className={`h-4 w-4 shrink-0 ${tone}`} />
      </div>
      <div className={`mt-2 text-2xl font-bold ${tone}`}>{value ?? 0}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-400">{sub}</div>}
    </button>
  );
}

function Panel({ title, icon, tone = "text-slate-500", children, badge, testid }) {
  const Ic = Icons[icon] || Icons.Square;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Ic className={`h-4 w-4 ${tone}`} />
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {badge && <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 uppercase tracking-wide">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-800" }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
      <div className={`text-xl font-bold ${tone}`}>{value ?? 0}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

function AwaitingData({ note }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="awaiting-data">
      <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center mb-2">
        <Icons.Hourglass className="h-4 w-4 text-slate-400" />
      </div>
      <div className="text-xs font-semibold text-slate-500">Awaiting Data</div>
      <div className="text-[11px] text-slate-400 mt-1 max-w-xs">{note}</div>
    </div>
  );
}

export default function CommsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const isSettingsRole = SETTINGS_ROLES.includes(user?.role);

  // Catch-all for legacy conversation deep-links (/communications?conversation=<id>)
  useEffect(() => {
    const conv = params.get("conversation");
    if (conv) navigate(`/communications/center?conversation=${conv}`, { replace: true });
  }, []); // eslint-disable-line

  const load = () => api.get("/communications/dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const k = d?.kpis || {};
  const na = d?.needs_attention || {};
  const penny = d?.penny_performance || {};
  const allison = d?.allison_performance || {};
  const sat = d?.client_satisfaction || {};
  const channels = Object.entries(d?.channels || {});
  const maxCh = channels.reduce((m, [, v]) => Math.max(m, v), 1);
  const workload = d?.team_workload || [];
  const trend = d?.trends?.messages_per_day || [];
  const maxTrend = trend.reduce((m, x) => Math.max(m, x.count), 1);
  const funnel = d?.lead_funnel || {};

  return (
    <div className="space-y-6" data-testid="comms-dashboard">
      {/* Workspace tab bar */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 pb-0 -mb-2" data-testid="comms-workspace-tabs">
        <button data-testid="ws-tab-inbox" onClick={() => navigate("/communications/center")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.MessagesSquare className="h-4 w-4" />Communications Center
        </button>
        <button data-testid="ws-tab-leads" onClick={() => navigate("/communications/center")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.UserPlus className="h-4 w-4" />Lead Messages
        </button>
        <button data-testid="ws-tab-clients" onClick={() => navigate("/communications/center")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.Users className="h-4 w-4" />Client Messages
        </button>
        <button data-testid="ws-tab-revisions" onClick={() => navigate("/communications/revision-requests")}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors">
          <Icons.RotateCcw className="h-4 w-4" />Revision Requests
        </button>
        {isSettingsRole && (
          <div className="relative ml-auto">
            <button data-testid="ws-tab-admin" onClick={() => setAdminOpen((v) => !v)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${adminOpen ? "border-slate-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"}`}>
              <Icons.Settings2 className="h-4 w-4" />Administration
              <Icons.ChevronDown className={`h-3.5 w-3.5 transition-transform ${adminOpen ? "rotate-180" : ""}`} />
            </button>
            {adminOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2" data-testid="comms-admin-panel">
                <button data-testid="comms-admin-link-settings" onClick={() => { setAdminOpen(false); navigate("/communications/settings"); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <Icons.Settings2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="font-medium">Comms Settings</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Communications</h1>
          <p className="text-sm text-slate-500 mt-1.5">Firm-wide messaging command center — leads, clients, Penny &amp; Allison.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/communications/center")} data-testid="comms-open-inbox"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
            <Icons.MessagesSquare className="h-4 w-4" /> Communications Center
          </button>
          <button onClick={() => navigate("/communications/center")} data-testid="comms-open-leads"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
            <Icons.UserPlus className="h-4 w-4" /> Lead Messages
          </button>
          <button onClick={() => navigate("/communications/center")} data-testid="comms-open-clients"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
            <Icons.Users className="h-4 w-4" /> Client Messages
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-rose-600" data-testid="comms-dashboard-error">Couldn't load messaging metrics. Retrying…</div>}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi label="New Inquiries" value={k.new_inquiries} icon="Flame" tone="text-orange-600" ring={k.new_inquiries ? "ring-orange-200" : "ring-slate-200"} onClick={() => navigate("/communications/center")} testid="kpi-new-inquiries" />
        <Kpi label="Unread Lead" value={k.unread_lead} icon="UserPlus" tone="text-blue-600" ring={k.unread_lead ? "ring-blue-200" : "ring-slate-200"} onClick={() => navigate("/communications/center")} testid="kpi-unread-lead" />
        <Kpi label="Unread Client" value={k.unread_client} icon="Users" tone="text-violet-600" ring={k.unread_client ? "ring-violet-200" : "ring-slate-200"} onClick={() => navigate("/communications/center")} testid="kpi-unread-client" />
        <Kpi label="Waiting on Staff" value={k.waiting_on_staff} icon="UserCog" tone={k.waiting_on_staff ? "text-amber-600" : "text-slate-900"} testid="kpi-waiting-staff" />
        <Kpi label="Waiting on Client" value={k.waiting_on_client} icon="Clock" testid="kpi-waiting-client" />
        <Kpi label="Escalated" value={k.escalated} icon="AlertTriangle" tone={k.escalated ? "text-rose-600" : "text-slate-900"} ring={k.escalated ? "ring-rose-200" : "ring-slate-200"} testid="kpi-escalated" />
        <Kpi label="Assigned to Penny" value={k.penny_assigned} icon="Bot" tone="text-indigo-600" onClick={() => navigate("/communications/center")} testid="kpi-penny" />
        <Kpi label="Assigned to Allison" value={k.allison_assigned} icon="Sparkles" tone="text-fuchsia-600" testid="kpi-allison" />
        <Kpi label="Open Conversations" value={k.open} icon="Inbox" testid="kpi-open" />
        <Kpi label="Closed Today" value={k.closed_today} icon="CheckCircle2" tone="text-emerald-600" testid="kpi-closed-today" />
        <Kpi label="Messages Today" value={k.messages_today} icon="MessageSquare" testid="kpi-msg-today" />
        <Kpi label="Messages This Week" value={k.messages_week} icon="CalendarDays" testid="kpi-msg-week" />
        <Kpi label="Avg First Response" value={fmtMin(k.avg_first_response_min)} icon="Timer" tone="text-blue-600" testid="kpi-first-response" />
        <Kpi label="Avg Resolution" value={fmtMin(k.avg_resolution_min)} icon="Gauge" tone="text-emerald-600" testid="kpi-resolution" />
      </div>

      {/* Lead conversion funnel */}
      <Panel title="Lead Conversion Funnel" icon="Filter" tone="text-blue-600" testid="widget-lead-funnel">
        <p className="text-xs text-slate-400 mb-4 -mt-2">How Penny &amp; website leads turn into paying clients.</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-stretch">
          {/* Stage 1 */}
          <div className="md:col-span-1 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100/40 border border-orange-200 p-4" data-testid="funnel-new-inquiries">
            <div className="flex items-center gap-1.5 text-orange-600"><Icons.Flame className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wide">New Inquiries</span></div>
            <div className="mt-2 text-3xl font-bold text-orange-700">{funnel.new_inquiries ?? 0}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">leads captured</div>
          </div>
          {/* Arrow + rate */}
          <div className="flex md:flex-col items-center justify-center gap-1 text-slate-400" data-testid="funnel-rate-booked">
            <Icons.ArrowRight className="h-5 w-5 md:rotate-0 rotate-90" />
            <span className="text-xs font-semibold text-blue-600">{funnel.booked_rate ?? 0}%</span>
          </div>
          {/* Stage 2 */}
          <div className="md:col-span-1 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100/40 border border-blue-200 p-4" data-testid="funnel-consult-booked">
            <div className="flex items-center gap-1.5 text-blue-600"><Icons.CalendarCheck className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wide">Consult Booked</span></div>
            <div className="mt-2 text-3xl font-bold text-blue-700">{funnel.consult_booked ?? 0}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{funnel.booked_to_engaged_rate ?? 0}% go on to engage</div>
          </div>
          {/* Arrow + rate */}
          <div className="flex md:flex-col items-center justify-center gap-1 text-slate-400" data-testid="funnel-rate-engaged">
            <Icons.ArrowRight className="h-5 w-5 md:rotate-0 rotate-90" />
            <span className="text-xs font-semibold text-emerald-600">{funnel.booked_to_engaged_rate ?? 0}%</span>
          </div>
          {/* Stage 3 */}
          <div className="md:col-span-1 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200 p-4" data-testid="funnel-engaged-clients">
            <div className="flex items-center gap-1.5 text-emerald-600"><Icons.BadgeCheck className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wide">Engaged Clients</span></div>
            <div className="mt-2 text-3xl font-bold text-emerald-700">{funnel.engaged_clients ?? 0}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">paying clients</div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-x-8 gap-y-1 text-xs text-slate-500">
          <span>Overall conversion (inquiry → client): <b className="text-emerald-600">{funnel.engaged_rate ?? 0}%</b></span>
          <span>Booking rate (inquiry → consult): <b className="text-blue-600">{funnel.booked_rate ?? 0}%</b></span>
        </div>
      </Panel>

      {/* row: needs attention + channel activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Needs Immediate Attention" icon="Siren" tone="text-rose-600" testid="widget-needs-attention">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <Stat label="SLA Breached" value={na.sla_breached} tone="text-rose-600" />
            <Stat label="Needs Human" value={na.needs_human} tone="text-orange-600" />
            <Stat label="Escalated" value={na.escalated} tone="text-rose-600" />
            <Stat label="Awaiting 1st Reply" value={na.awaiting_first_response} tone="text-amber-600" />
          </div>
        </Panel>

        <Panel title="Channel Activity" icon="Radio" tone="text-emerald-600" testid="widget-channel-activity">
          <div className="space-y-2.5">
            {channels.length === 0 && <div className="text-sm text-slate-400">No conversations yet.</div>}
            {channels.map(([chn, v]) => {
              const meta = CH_META[chn] || { label: chn, icon: "MessageSquare", tone: "text-slate-500" };
              const Ic = Icons[meta.icon] || Icons.MessageSquare;
              return (
                <div key={chn} className="flex items-center gap-3" data-testid={`channel-row-${chn}`}>
                  <Ic className={`h-4 w-4 shrink-0 ${meta.tone}`} />
                  <span className="text-sm text-slate-600 w-32 shrink-0 truncate">{meta.label}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full bg-current ${meta.tone}`} style={{ width: `${Math.round((v / maxCh) * 100)}%` }} />
                  </div>
                  <span className="text-sm font-semibold text-slate-700 w-8 text-right">{v}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      {/* row: team workload + trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Team Workload" icon="Users" tone="text-blue-600" testid="widget-team-workload">
          <div className="space-y-2.5">
            {workload.length === 0 && <div className="text-sm text-slate-400">No open conversations.</div>}
            {workload.map((t) => (
              <div key={t.team} className="flex items-center justify-between" data-testid={`team-row-${t.team}`}>
                <span className="text-sm text-slate-600">{t.name}</span>
                <span className="text-sm font-semibold text-slate-800 bg-slate-100 rounded-full px-2.5 py-0.5">{t.count}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Communication Trends" icon="TrendingUp" tone="text-indigo-600" testid="widget-trends">
          <div className="text-xs text-slate-400 mb-2">Messages per day (last 7 days)</div>
          <div className="flex items-end gap-2 h-28">
            {trend.length === 0 && <div className="text-sm text-slate-400">No data.</div>}
            {trend.map((x) => (
              <div key={x.date} className="flex-1 flex flex-col items-center gap-1" title={`${x.date}: ${x.count}`}>
                <div className="w-full bg-indigo-500/80 rounded-t" style={{ height: `${Math.max(4, Math.round((x.count / maxTrend) * 96))}px` }} />
                <span className="text-[9px] text-slate-400">{x.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* row: Penny + Allison + Satisfaction */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel title="Penny Performance" icon="Bot" tone="text-indigo-600" testid="widget-penny">
          <div className="grid grid-cols-2 gap-2.5">
            <Stat label="Conversations Handled" value={penny.conversations_handled} tone="text-indigo-600" />
            <Stat label="Inquiries Created" value={penny.inquiries_created} />
            <Stat label="Appointments Booked" value={penny.appointments_booked} tone="text-emerald-600" />
            <Stat label="Quotes Requested" value={penny.quotes_requested} />
            <Stat label="Resolved" value={penny.resolved} tone="text-emerald-600" />
            <Stat label="Escalated" value={penny.escalated} tone="text-rose-600" />
          </div>
        </Panel>

        <Panel title="Allison Performance" icon="Sparkles" tone="text-fuchsia-600" badge={allison.awaiting_data ? "Coming Online" : null} testid="widget-allison">
          {allison.awaiting_data
            ? <AwaitingData note="Populates automatically once Allison activity logging is enabled — drafts, summaries, recommendations & time saved." />
            : (
              <div className="grid grid-cols-2 gap-2.5">
                <Stat label="Conversations Assisted" value={allison.conversations_assisted} tone="text-fuchsia-600" />
                <Stat label="Draft Replies" value={allison.draft_replies} />
                <Stat label="Client Summaries" value={allison.client_summaries} />
                <Stat label="Executive Summaries" value={allison.executive_summaries} />
                <Stat label="Production Recs" value={allison.production_recommendations} />
                <Stat label="Service Recs" value={allison.service_recommendations} />
              </div>
            )}
        </Panel>

        <Panel title="Client Satisfaction" icon="Star" tone="text-amber-500" badge={sat.awaiting_data ? "Collecting Data" : null} testid="widget-satisfaction">
          {sat.awaiting_data
            ? <AwaitingData note="Data collection begins once post-engagement review requests are activated — ratings, reviews, NPS & CSAT." />
            : (
              <div className="grid grid-cols-2 gap-2.5">
                <Stat label="Average Rating" value={sat.average_rating ?? "—"} tone="text-amber-500" />
                <Stat label="Total Reviews" value={sat.total_reviews} />
                <Stat label="Google Reviews" value={sat.google_reviews} />
                <Stat label="Portal Reviews" value={sat.portal_reviews} />
              </div>
            )}
        </Panel>
      </div>
    </div>
  );
}
